import { Form, Button, Alert, InputGroup, Table, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import React, { useEffect, useState, useMemo } from 'react';
import { login, setIsLoading } from '../general/actions';
import AlertModal from '../general/alertModal';
import { useNavigate } from 'react-router-dom';
import { currencyFormater } from '../general/utils';
import { C, barColor } from '../general/colors';

function getInitials(name) {
    if (!name) return 'U';
    return name.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

function toWhatsappNumber(phone) {
    const digits = (phone || '').replace(/\D/g, '');
    if (digits.length === 10) return '57' + digits; // celular CO sin indicativo
    return digits.length >= 11 ? digits : null;
}

function Users(props) {
    const { setIsLoading, roles, userId } = props;

    const [newUserEmail, setNewUserEmail] = useState('');
    const [users, setUsers] = useState([]);
    const [organization, setOrganization] = useState('');
    const [organizations, setOrganizations] = useState([]);
    const [rolesList, setRolesList] = useState([]);
    const [departmentsList, setDepartmentsList] = useState([]);
    const [fundsList, setFundsList] = useState([]);
    const [agreementsList, setAgreementsList] = useState([]);
    const [selectedAgreement, setSelectedAgreement] = useState('');

    const [alertModalShow, setAlertModalShow] = useState(false);
    const [alertModalMsg, setAlertModalMsg] = useState('');
    const [alertModalType, setAlertModalType] = useState('');
    const [alertModalOnConfirm, setAlertModalOnConfirm] = useState(null);
    const [alertModalOnClose, setAlertModalOnClose] = useState(null);

    // { user, url, expiresAt, emailSent } del último enlace generado
    const [resetLink, setResetLink] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);

    const navigate = useNavigate();

    const getOrganizations = async () => {
        setIsLoading(true);
        try {
            const r = await axiosInstance.get('/organizations');
            setOrganizations(r.data);
            if (r.data.length === 1) {
                setOrganization(r.data[0].id);
            }
        }
        catch (e) { console.log(e); } finally { setIsLoading(false); }
    };

    const getRoleList = async () => {
        setIsLoading(true);
        try { const r = await axiosInstance.get('/roles'); setRolesList(r.data); }
        catch (e) { console.log(e); } finally { setIsLoading(false); }
    };

    const getDepartmentList = async () => {
        setIsLoading(true);
        try { const r = await axiosInstance.get('/departments/current-company-user'); setDepartmentsList(r.data); }
        catch (e) { console.log(e); } finally { setIsLoading(false); }
    };

    const getFundsList = async () => {
        try {
            const agrRes = await axiosInstance.get('/agreements/current-organization');
            const agreements = agrRes.data || [];
            setAgreementsList(agreements);
            const fundArrays = await Promise.all(
                agreements.map(a =>
                    axiosInstance.get('/funds/agreements/' + a.id)
                        .then(r => (r.data || []).map(f => ({ ...f, agreement_id: a.id })))
                        .catch(() => [])
                )
            );
            setFundsList(fundArrays.flat());
        } catch (e) { console.log(e); }
    };

    const getUsersByOrganization = async () => {
        setIsLoading(true);
        try {
            const r = await axiosInstance.get('/users/organizations/' + organization);
            const usersWithFunds = await Promise.all(
                (r.data || []).map(async user => {
                    if (!selectedAgreement) {
                        return { ...user, funds: [] };
                    }
                    try {
                        const fr = await axiosInstance.get('users-funds/user/' + user.id);
                        return { ...user, funds: fr.data || [] };
                    } catch { return { ...user, funds: [] }; }
                })
            );
            setUsers(usersWithFunds);
        } catch (e) { console.log(e); } finally { setIsLoading(false); }
    };

    const autologin = async (id, confirmed) => {
        if (!confirmed) {
            setAlertModalShow(true); setAlertModalType('');
            setAlertModalMsg('¿Está seguro de hacer autologin con el usuario ' + id + '?');
            setAlertModalOnConfirm(() => () => { autologin(id, true); });
            return;
        }
        setIsLoading(true);
        try {
            const response = await axiosInstance.post('/autologin', { id });
            setIsLoading(false);
            login(response.data.user);
            navigate('/main');
            window.location.reload();
        } catch (error) {
            setAlertModalShow(true); setAlertModalType('danger');
            setAlertModalMsg(error.response.data.message.includes('foreign')
                ? 'No se puede realizar el autologin'
                : error.response.data.message);
        } finally { setIsLoading(false); }
    };

    const addUser = async (email) => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.put('/users/add-organization', { email, organization_id: organization });
            let msg = 'Usuario invitado correctamente';
            if (response.data.message === 'User successfully configured') {
                msg = 'Usuario asociado correctamente';
                getUsersByOrganization();
            }
            setAlertModalType('success');
            setAlertModalShow(true);
            setAlertModalMsg(msg);
            setNewUserEmail('');
        } catch (error) {
            setAlertModalType('danger');
            setAlertModalShow(true);
            setAlertModalMsg(
                error.response.data['message'] === 'User already belongs to other organization'
                    ? 'El usuario ya pertenece a otra entidad'
                    : 'Error en el servidor, intente de nuevo'
            );
        } finally { setIsLoading(false); }
    };

    const showUserActionError = (error, fallback) => {
        const status = error.response?.status;
        setAlertModalShow(true); setAlertModalType('danger');
        setAlertModalMsg(
            status === 403 ? 'No tienes permisos para realizar esta acción sobre este usuario'
            : status === 404 ? 'El usuario no existe o no pertenece a tu entidad'
            : status === 422 ? (error.response?.data?.message || fallback)
            : fallback
        );
    };

    const deleteUser = async (user, confirmed) => {
        if (!confirmed) {
            setAlertModalShow(true); setAlertModalType('');
            setAlertModalMsg('¿Desactivar al usuario ' + (user.name || '') + ' (' + user.email + ')? No podrá iniciar sesión hasta que sea activado de nuevo.');
            setAlertModalOnConfirm(() => () => { deleteUser(user, true); });
            return;
        }
        const id = user.id;
        setIsLoading(true);
        try { await axiosInstance.delete('/users/' + id); getUsersByOrganization(); }
        catch (e) { showUserActionError(e, 'No fue posible inactivar el usuario'); } finally { setIsLoading(false); }
    };

    const activateUser = async (id) => {
        setIsLoading(true);
        try { await axiosInstance.put('/users/activate/' + id); getUsersByOrganization(); }
        catch (e) { showUserActionError(e, 'No fue posible activar el usuario'); } finally { setIsLoading(false); }
    };

    const writeClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Fallback para contextos sin Clipboard API (http) o sin gesto de usuario vigente
            try {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.setAttribute('readonly', '');
                ta.style.position = 'fixed'; ta.style.opacity = '0';
                document.body.appendChild(ta); ta.select();
                const ok = document.execCommand('copy');
                document.body.removeChild(ta);
                return ok;
            } catch { return false; }
        }
    };

    // mode: 'email' -> genera, envía por correo y muestra el enlace
    //       'copy'  -> genera SIN enviar correo y lo copia al portapapeles
    const generatePasswordResetLink = async (user, mode, confirmed) => {
        if (!confirmed) {
            setAlertModalShow(true); setAlertModalType('');
            setAlertModalMsg(
                (mode === 'email'
                    ? '¿Enviar por correo un enlace para restablecer la contraseña de '
                    : '¿Generar un enlace para restablecer la contraseña de ')
                + user.email + '? El enlace anterior, si existe, quedará invalidado.'
            );
            setAlertModalOnConfirm(() => () => { generatePasswordResetLink(user, mode, true); });
            return;
        }
        setIsLoading(true);
        try {
            const { data } = await axiosInstance.post(`/users/${user.id}/password-reset-link`, { send_email: mode === 'email' });
            const copied = mode === 'copy' ? await writeClipboard(data.url) : false;
            setLinkCopied(copied);
            setResetLink({ user, mode, url: data.url, expiresAt: data.expires_at, emailSent: data.email_sent });
        } catch (error) {
            setAlertModalShow(true); setAlertModalType('danger');
            setAlertModalMsg(error.response?.status === 429
                ? 'Demasiados intentos. Espera un minuto e intenta de nuevo.'
                : 'No fue posible generar el enlace. Intenta de nuevo.');
        } finally { setIsLoading(false); }
    };

    const copyResetLink = async () => {
        setLinkCopied(await writeClipboard(resetLink.url));
    };

    const addRemoveRol = async (user_id, role_id) => {
        if (!role_id) return;
        setIsLoading(true);
        try { await axiosInstance.put('/users/add-remove-rol', { user_id, role_id }); getUsersByOrganization(); }
        catch (e) { showUserActionError(e, 'No fue posible actualizar el rol'); } finally { setIsLoading(false); }
    };


    const refreshUserFunds = async (user_id) => {
        try {
            const r = await axiosInstance.get('users-funds/user/' + user_id);
            setUsers(prev => prev.map(u => u.id === user_id ? { ...u, funds: r.data || [] } : u));
        } catch (e) { console.log(e); }
    };

    const addFundUser = async (user_id, fund_id) => {
        if (!fund_id) return;
        setIsLoading(true);
        try {
            await axiosInstance.post('users-funds', { user_id, fund_id });
            await refreshUserFunds(user_id);
        } catch (error) {
            setAlertModalShow(true);
            setAlertModalType('danger');
            setAlertModalMsg(
                error.response?.data?.message?.includes('Duplicate')
                    ? 'El fondo ya está asignado a este usuario'
                    : 'Error al asignar fondo'
            );
        } finally { setIsLoading(false); }
    };

    const removeFundUser = async (userFundId, user_id) => {
        setIsLoading(true);
        try {
            await axiosInstance.delete('users-funds/' + userFundId);
            await refreshUserFunds(user_id);
        } catch (e) { console.log(e); } finally { setIsLoading(false); }
    };

    const addRemoveDepartment = async (user_id, department_id) => {
        if (!department_id) return;
        setIsLoading(true);
        try { await axiosInstance.put('/users/add-remove-department', { user_id, department_id, organization }); getUsersByOrganization(); }
        catch (e) { console.log(e); } finally { setIsLoading(false); }
    };

    useEffect(() => {
        if (organization) {
            getUsersByOrganization();
            (async () => {
                try {
                    const url = roles.includes('superadmin')
                        ? `/agreements/organization/${organization}`
                        : '/agreements/current-organization';
                    const agrRes = await axiosInstance.get(url);
                    setAgreementsList(agrRes.data || []);
                } catch (e) { console.log(e); }
            })();
        } else {
            setUsers([]);
            setFundsList([]);
            setAgreementsList([]);
            setSelectedAgreement('');
        }
    }, [organization, roles, selectedAgreement]);

    // Load funds when a contract is selected
    useEffect(() => {
        if (selectedAgreement && organization) {
            (async () => {
                try {
                    const r = await axiosInstance.get(`/funds/agreements/${selectedAgreement}`);
                    setFundsList((r.data || []).map(f => ({ ...f, agreement_id: parseInt(selectedAgreement) })));
                } catch (e) { console.log(e); }
            })();
        } else {
            setFundsList([]);
        }
    }, [selectedAgreement, organization]);

    useEffect(() => {
        getOrganizations(); getRoleList(); getDepartmentList();
    }, []);

    // Reload departments whenever a contract is selected
    useEffect(() => {
        if (selectedAgreement) {
            getDepartmentList();
        }
    }, [selectedAgreement]);

    const fundAgreementMap = useMemo(() => {
        const map = {};
        fundsList.forEach(f => { map[f.id] = f.agreement_id; });
        return map;
    }, [fundsList]);

    return (
        <>
            {
    /* Header */
    <div className="order-page-header">
        <div className="order-page-title">
            <i className="fa fa-users" style={{ color: 'var(--abi-primary)' }} />
            Gestión de Usuarios
        </div>
    </div>,

    /* Org selector + add user */
    <div className="order-section">
        <div className="order-section-header"><i className="fa fa-building-o" /> Seleccionar entidad</div>
        <div className="order-section-body">
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 2, minWidth: 220 }}>
                    <Form.Label className="report-field-label">Entidad</Form.Label>
                    <Form.Select value={organization} onChange={(e) => setOrganization(e.target.value)}>
                        <option value="">— Selecciona una entidad —</option>
                        {organizations.map(o => (
                            <option key={'org-' + o.id} value={o.id}>
                                {o.id}: {o.ext_id} — {o.name}
                            </option>
                        ))}
                    </Form.Select>
                </div>

                {organization && (
                    <div style={{ flex: 1, minWidth: 280 }}>
                        <Form.Label className="report-field-label">Asociar / Invitar usuario por email</Form.Label>
                        <InputGroup>
                            <Form.Control
                                type="email" value={newUserEmail}
                                placeholder="correo@entidad.com"
                                onChange={(e) => setNewUserEmail(e.target.value)}
                            />
                            <Button variant="primary" disabled={!newUserEmail} onClick={() => addUser(newUserEmail)}>
                                <i className="fa fa-user-plus" /> Asociar
                            </Button>
                        </InputGroup>
                    </div>
                )}
            </div>
        </div>
    </div>
}

            {/* Users table */}
            {users.length > 0 && (
                <div className="order-section" style={{ marginTop: 16 }}>
                    <div className="order-section-header" style={{ justifyContent: 'space-between' }}>
                        <span><i className="fa fa-list" /> {users.length} usuario{users.length !== 1 ? 's' : ''}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--abi-muted)', whiteSpace: 'nowrap' }}>
                                <i className="fa fa-filter" /> Contrato:
                            </span>
                            <Form.Select size="sm" value={selectedAgreement}
                                onChange={e => setSelectedAgreement(e.target.value)}
                                style={{ minWidth: 200, fontSize: 12, fontWeight: 'normal', textTransform: 'none', letterSpacing: 0 }}>
                                    <option value="">Seleccionar contrato</option>
                                {agreementsList.map(a => (
                                    <option key={a.id} value={a.id}>
                                        {a.ext_id} — {a.description}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    </div>
                    <Table responsive className="report-table mb-0" style={{ borderRadius: 0, border: 'none' }}>
                        <thead>
                            <tr>
                                <th>Usuario</th>
                                <th>Teléfono</th>
                                <th>Roles</th>
                                <th>Centros de costo</th>
                                <th>Estado</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <React.Fragment key={'user-' + user.id}>
                                    <tr>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 12, flexShrink: 0 }}>
                                                    {getInitials(user.name)}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{user.name}</div>
                                                    <div style={{ fontSize: 11, color: 'var(--abi-muted)' }}>{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ fontSize: 12 }}>{user.phone}</td>
                                        <td>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                {user.user_role.length ? user.user_role.map(rol => (
                                                    <span key={'rol-' + rol.id} className="role-chip" style={{ gap: 4 }}>
                                                        {userId !== user.id && (
                                                            <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.danger, lineHeight: 1, fontSize: 10 }}
                                                                onClick={() => addRemoveRol(user.id, rol.role.id)} title="Quitar rol">
                                                                <i className="fa fa-times-circle" />
                                                            </button>
                                                        )}
                                                        {rol.role.description}
                                                    </span>
                                                )) : <span style={{ fontSize: 12, color: 'var(--abi-muted)' }}>Sin roles</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                {user.user_department.length ? user.user_department.map(d => (
                                                    <span key={'dep-' + d.id} className="dept-chip" style={{ fontSize: 11 }}>
                                                        {d.department.name}
                                                    </span>
                                                )) : <span style={{ fontSize: 12, color: 'var(--abi-muted)' }}>—</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="report-status-badge" style={
                                                !user.deleted_at
                                                    ? { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }
                                                    : { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }
                                            }>
                                                {user.deleted_at ? 'Inactivo' : 'Activo'}
                                            </span>
                                        </td>
                                        <td>
                                            {userId !== user.id && (
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    {user.deleted_at
                                                        ? <button className="report-icon-btn" style={{ color: C.success }} title="Activar" onClick={() => activateUser(user.id)}>
                                                            <i className="fa fa-check" />
                                                          </button>
                                                        : <button className="report-icon-btn" style={{ color: C.danger }} title="Inactivar" onClick={() => deleteUser(user)}>
                                                            <i className="fa fa-ban" />
                                                          </button>
                                                    }
                                                    {!user.deleted_at && (
                                                        <>
                                                            <button className="report-icon-btn" title="Enviar por correo enlace para restablecer contraseña" onClick={() => generatePasswordResetLink(user, 'email')}>
                                                                <i className="fa fa-key" />
                                                            </button>
                                                            <button className="report-icon-btn" title="Copiar enlace para restablecer contraseña (sin enviar correo)" onClick={() => generatePasswordResetLink(user, 'copy')}>
                                                                <i className="fa fa-link" />
                                                            </button>
                                                        </>
                                                    )}
                                                    {roles.includes('superadmin') && (
                                                        <button className="report-icon-btn" title="Autologin" onClick={() => autologin(user.id)}>
                                                            <i className="fa fa-sign-in" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Inline controls row */}
                                    <tr style={{ background: '#fafbff' }}>
                                        {/* Fondos asignados + selector — visible solo con el switch activo */}
                                        <td colSpan={2}>
                                            {selectedAgreement && user?.funds?.length > 0 && (() => {
                                                const visibleFunds = selectedAgreement
                                                    ? user.funds.filter(uf => fundAgreementMap[uf.fund_id] === parseInt(selectedAgreement))
                                                    : user.funds;
                                                if (!visibleFunds.length) return null;
                                                return (
                                                    <div style={{ marginBottom: 8 }}>
                                                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--abi-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                                                            Fondos asignados
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                            {visibleFunds.map(uf => {
                                                                const pct = uf.fund.amount > 0 ? Math.min(100, (uf.fund.spent / uf.fund.amount) * 100) : 0;
                                                                const bc = barColor(pct);
                                                                const agreement = agreementsList.find(a => a.id === fundAgreementMap[uf.fund_id]);
                                                                return (
                                                                    <div key={'uf-' + uf.id} className="config-user-row" style={{ padding: '6px 8px' }}>
                                                                        <div style={{ flex: 1, fontSize: 12 }}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                                                                <div>
                                                                                    <span style={{ fontWeight: 600 }}>{uf.fund.name}</span>
                                                                                    {agreement && (
                                                                                        <span style={{ fontSize: 10, color: 'var(--abi-primary)', marginLeft: 6, background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.2)', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                                                                                            {agreement.ext_id}{agreement.description ? ` — ${agreement.description}` : ''}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <span style={{ color: bc, fontWeight: 700 }}>{pct.toFixed(0)}%</span>
                                                                            </div>
                                                                            <div className="report-fund-bar-wrap">
                                                                                <div className="report-fund-bar-fill" style={{ width: `${pct}%`, background: bc }} />
                                                                            </div>
                                                                            <div style={{ display: 'flex', gap: 10, marginTop: 2, color: 'var(--abi-muted)', fontSize: 11 }}>
                                                                                <span>Ini: {currencyFormater(uf.fund.amount)}</span>
                                                                                <span>Cons: {currencyFormater(uf.fund.spent)}</span>
                                                                                <span style={{ color: parseFloat(uf.fund.available) <= 0 ? C.danger : C.success, fontWeight: 600 }}>
                                                                                    Disp: {currencyFormater(uf.fund.available)}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            className="order-detail-delete-btn"
                                                                            title="Quitar fondo"
                                                                            onClick={() => removeFundUser(uf.id, user.id)}
                                                                        >
                                                                            <i className="fa fa-times" />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            {/* Selector para asignar fondo — solo visible con el switch activo */}
                                            {selectedAgreement && (() => {
                                                const userDeptIds = new Set(
                                                    user.user_department.map(d => d.department.id)
                                                );
                                                const assignedFundIds = new Set(
                                                    (user.funds || []).map(uf => uf.fund_id)
                                                );
                                                const availableFunds = fundsList.filter(f =>
                                                    userDeptIds.has(f.department_id) &&
                                                    !assignedFundIds.has(f.id) &&
                                                    (!selectedAgreement || f.agreement_id === parseInt(selectedAgreement))
                                                );
                                                return (
                                                    <Form.Select size="sm"
                                                        onChange={(e) => { addFundUser(user.id, e.target.value); e.target.value = ''; }}
                                                        style={{ fontSize: 12 }}
                                                        disabled={userDeptIds.size === 0}>
                                                        <option value="">
                                                            {userDeptIds.size === 0
                                                                ? 'Sin centros de costo asignados'
                                                                : availableFunds.length
                                                                    ? 'Asignar fondo…'
                                                                    : selectedAgreement
                                                                        ? 'Sin fondos disponibles para este contrato'
                                                                        : 'Todos los fondos ya asignados'}
                                                        </option>
                                                        {availableFunds.map(f => (
                                                            <option key={'fl-' + f.id} value={f.id} disabled={!f.is_active}>
                                                                {f.ext_id} — {f.name}
                                                                {f.department?.name ? ` (${f.department.name})` : ''}
                                                                {!f.is_active ? ' — inactivo' : ''}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                );
                                            })()}
                                        </td>

                                        {/* Selector de rol */}
                                        <td colSpan={2}>
                                            {userId !== user.id && (
                                                <Form.Select size="sm"
                                                    onChange={(e) => { addRemoveRol(user.id, e.target.value); e.target.value = ''; }}
                                                    style={{ fontSize: 12 }}>
                                                    <option value="">Agregar / quitar rol…</option>
                                                    {rolesList.map(r => (
                                                        <option key={'rl-' + r.id} value={r.id}>{r.description}</option>
                                                    ))}
                                                </Form.Select>
                                            )}
                                        </td>

                                        {/* Selector de centro de costo */}
                                        <td colSpan={2}>
                                            <Form.Select size="sm"
                                                onChange={(e) => { addRemoveDepartment(user.id, e.target.value); e.target.value = ''; }}
                                                style={{ fontSize: 12 }}>
                                                <option value="">Agregar / quitar centro de costo…</option>
                                                {departmentsList.map(d => (
                                                    <option key={'dl-' + d.id} value={d.id}>{d.name}</option>
                                                ))}
                                            </Form.Select>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={!!resetLink} onHide={() => setResetLink(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 16 }}>
                        <i className="fa fa-key" /> Enlace de restablecimiento
                    </Modal.Title>
                </Modal.Header>
                {resetLink && (() => {
                    return (
                        <Modal.Body style={{ fontSize: 13 }}>
                            {resetLink.mode === 'email' ? (
                                <Alert variant={resetLink.emailSent ? 'success' : 'warning'} className="py-2" style={{ fontSize: 13 }}>
                                    {resetLink.emailSent
                                        ? <>Se envió el enlace a <strong>{resetLink.user.email}</strong>. Si no lo recibe, compártelo por otro medio.</>
                                        : <>No se pudo enviar el correo a <strong>{resetLink.user.email}</strong>. Comparte el enlace por otro medio.</>}
                                </Alert>
                            ) : (
                                <Alert variant={linkCopied ? 'success' : 'info'} className="py-2" style={{ fontSize: 13 }}>
                                    {linkCopied
                                        ? <>Enlace copiado al portapapeles. Pégalo en el medio que prefieras para enviarlo a <strong>{resetLink.user.email}</strong>.</>
                                        : <>Enlace generado para <strong>{resetLink.user.email}</strong>. Cópialo y compártelo por el medio que prefieras.</>}
                                </Alert>
                            )}
                            <InputGroup className="mb-2">
                                <Form.Control readOnly value={resetLink.url} onFocus={(e) => e.target.select()} style={{ fontSize: 12 }} />
                                <Button variant={linkCopied ? 'success' : 'primary'} onClick={copyResetLink}>
                                    <i className={'fa ' + (linkCopied ? 'fa-check' : 'fa-copy')} /> {linkCopied ? 'Copiado' : 'Copiar'}
                                </Button>
                            </InputGroup>
                            <div style={{ color: 'var(--abi-muted)', fontSize: 12 }}>
                                Válido hasta {new Date(resetLink.expiresAt).toLocaleString('es-CO')} y de un solo uso.
                                Compártelo únicamente con el titular de la cuenta.
                            </div>
                        </Modal.Body>
                    );
                })()}
                <Modal.Footer>
                    {resetLink && toWhatsappNumber(resetLink.user.phone) && (
                        <Button variant="success" as="a" target="_blank" rel="noopener noreferrer"
                            href={`https://wa.me/${toWhatsappNumber(resetLink.user.phone)}?text=${encodeURIComponent(`Hola ${resetLink.user.name || ''}, usa este enlace para restablecer tu contraseña en Abi: ${resetLink.url}`)}`}>
                            <i className="fa fa-whatsapp" /> Enviar por WhatsApp
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setResetLink(null)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            <AlertModal
                show={alertModalShow} setShow={setAlertModalShow}
                msg={alertModalMsg} setMsg={setAlertModalMsg}
                onConfirm={alertModalOnConfirm} setOnConfirm={setAlertModalOnConfirm}
                onClose={alertModalOnClose} setOnClose={setAlertModalOnClose}
                variant={alertModalType}
            />
        </>
    );
}

function mapStateToProps(state) {
    return { isLoading: state.isLoading, userId: state.userId, roles: state.roles, departments: state.departments };
}

const mapDispatchToProps = { setIsLoading };
export default connect(mapStateToProps, mapDispatchToProps)(Users);
